import React from "react";

import { chainCommands } from "prosemirror-commands";
import { Node as ProseNode } from "prosemirror-model";
import { EditorState, Transaction } from "prosemirror-state";
import { EditorView } from "prosemirror-view";

import AppContext from "~/common/AppContext";
import { EditorConfig, ProseReact } from "~/lib/prose";
import { createSameTypeAfter, splitBlock } from "~/lib/prose/commands";
import { Extension } from "~/lib/prose/extension";
import { LinkMark } from "~/lib/prose/marks";
import { Mention, TextNode, Unstyled } from "~/lib/prose/nodes";
import { GlobalPlaceholder, InputRules, MentionProvider, Paste, TypeAhead } from "~/lib/prose/plugins";
import EmojiProvider, { PasteBuiltInEmoji } from "~/lib/prose/plugins/emoji";
import type { VirtualKeyboardDetector } from "~/lib/virtualKeyboard";
import { StreamUsersContext } from "~/notes/context";
import { eventEmitter } from "~/stream/Comments/events";

class NewLine extends Extension {
	name = "comment_newline";

	constructor(private virtualKeyboardDetector: VirtualKeyboardDetector) {
		super();
	}

	keys() {
		const returnCommand = chainCommands(createSameTypeAfter([]), splitBlock);

		return {
			"Shift-Enter": returnCommand,
			Enter: (state: EditorState, dispatch?: (tr: Transaction) => void, view?: EditorView) => {
				if (this.virtualKeyboardDetector.isVirtualKeyboard()) {
					return returnCommand(state, dispatch, view);
				}
				return false;
			},
		};
	}
}

/**
 * Prose extension that makes the Enter key run the `onSubmit` callback, if
 * we don't detect a virtual keyboard (e.g. on a phone).
 *
 * This uses a Prose extension instead of just a React keydown handler because
 * we need to check if any Prose plugins have already handled the Enter key.
 * For other keys we could use `e.defaultPrevented` to check, but Prose always
 * runs `preventDefault` on the enter key specifically, to normalize cross-
 * browser behaviours.
 * @see https://github.com/ProseMirror/prosemirror-view/blob/02d50622574053c6314b456f0cb0ae069fdb9089/src/capturekeys.ts#L327
 */
class EnterSubmits extends Extension {
	name = "comment_enter_submits";

	constructor(
		private virtualKeyboardDetector: VirtualKeyboardDetector,
		private onSubmitRef: React.MutableRefObject<() => void>,
	) {
		super();
	}

	keys() {
		return {
			Enter: (_state: EditorState, dispatch?: (tr: Transaction) => void) => {
				if (this.virtualKeyboardDetector.isVirtualKeyboard()) return false;
				// This is a ProseMirror Command, so if dispatch is not provided,
				// we aren't actually supposed to run the command.
				// https://prosemirror.net/docs/guide/#commands
				if (dispatch) this.onSubmitRef.current();
				return true;
			},
		};
	}
}

function CommentsEditor({
	doc,
	onChange,
	onSubmit,
	placeholder = "",
	configRef,
	editorRef,
	note,
	virtualKeyboardDetector,
	className,
	usersToMention,
}: {
	doc: ProseNode | { [key: string]: unknown };
	onChange: (v: ProseNode) => void;
	onSubmit: () => void;
	placeholder?: string;
	configRef?: React.MutableRefObject<EditorConfig | undefined>;
	editorRef: React.MutableRefObject<ProseReact | null>;
	note?: { id: string };
	virtualKeyboardDetector: VirtualKeyboardDetector;
	className?: string;
	usersToMention?: Array<{ id: string; fullName: string; email: string }>;
}) {
	const onSubmitRef = React.useRef(onSubmit); // Uses a ref so onSubmit can change without re-generating the editorConfig
	if (onSubmitRef.current !== onSubmit) onSubmitRef.current = onSubmit;
	const { user } = React.useContext(AppContext);
	const streamUsers = React.useContext(StreamUsersContext);
	const editorContext = React.useMemo(() => {
		return {
			participants: usersToMention || streamUsers.map(su => su.user),
		};
	}, [usersToMention, streamUsers]);
	const editorConfig = React.useMemo(() => {
		const config = new EditorConfig({
			nodes: { doc: { content: "block+" } },
			extensions: [
				new TextNode(),
				new Unstyled({ disableIndentInputRule: true }),
				new Paste(),
				new GlobalPlaceholder({ text: placeholder }),
				new InputRules(),
				new Mention({ currentUserId: user?.id }),
				new TypeAhead({
					mountToDocument: true,
					providers: [new MentionProvider(), new EmojiProvider()],
				}),
				new LinkMark({ allowEditing: false }),
				new PasteBuiltInEmoji(),
				new NewLine(virtualKeyboardDetector),
				new EnterSubmits(virtualKeyboardDetector, onSubmitRef), // Must be after NewLine and TypeAhead so they can override Enter
			],
		});
		if (configRef) {
			configRef.current = config;
		}
		return config;
	}, [placeholder, configRef, user?.id, virtualKeyboardDetector]);
	const onDocChange = React.useCallback(
		(editorState: EditorState, docChanged) => {
			if (docChanged) {
				onChange(editorState.doc);
			}
		},
		[onChange],
	);

	// this only runs when the component initially mounts
	React.useEffect(() => {
		editorRef?.current?.focus();
	}, [editorRef]);

	// so we need this to focus the editor if "Add a comment" is clicked while the editor is already open
	React.useEffect(() => {
		const onFocusComments = (noteId: string) => {
			if (noteId === note?.id) {
				editorRef?.current?.focus();
			}
		};
		eventEmitter.on("NoteComments.open", onFocusComments);

		return function cleanup() {
			eventEmitter.off("NoteComments.open", onFocusComments);
		};
	}, [editorRef, note?.id]);

	const controlledDoc = doc instanceof ProseNode ? doc : editorConfig.schema.nodeFromJSON(doc);
	return (
		<ProseReact
			ref={editorRef}
			config={editorConfig}
			controlledDoc={controlledDoc}
			onChange={onDocChange}
			className={className}
			context={editorContext}
			resetSelectionOnControlledDocChange={false}
		/>
	);
}

export default CommentsEditor;
